import { resolveConnectionRuntimeConfig, hasLiveCredentials } from "../runtime/connectionRuntimeConfigService";
import { saveConnectionVerificationHistory, listConnectionVerificationHistory } from "./connectionHistoryService";
import { SECRET_MASK } from "../constants";
import { runFullEngine, validateConfiguration } from "./connectionVerifyEngine";
import { resolveConnectionTestRuntime } from "./connectionTestRuntimeService";
import {
  FIREBASE_SDK_VERSION,
  IGNORABLE_STATUSES,
  OPTIONAL_SERVICES,
  detectBrowser,
  detectOs,
  makeServiceResult,
  nowIso,
} from "./connectionVerifyUtils";

export { FIREBASE_SDK_VERSION };
export {
  exportVerificationJson,
  exportVerificationCsv,
  exportVerificationHtml,
  exportVerificationPdf,
  exportVerificationMarkdown,
  formatVerificationReport,
} from "./connectionVerifyExport";
export { listConnectionVerificationHistory } from "./connectionHistoryService";

export const MONITOR_INTERVALS = [
  { value: 0, label: "Off" },
  { value: 5 * 60 * 1000, label: "5 min" },
  { value: 15 * 60 * 1000, label: "15 min" },
  { value: 30 * 60 * 1000, label: "30 min" },
  { value: 60 * 60 * 1000, label: "1 hour" },
];

const CORE_SERVICES = ["configuration", "authentication", "firestore", "storage"];

export function calculateHealthScore(services, diagnostics = []) {
  const configErrors = diagnostics.filter((d) => d.severity === "error" && d.category === "configuration");
  if (configErrors.length) return { score: 0, grade: "red" };

  const scorable = Object.entries(services).filter(([key, svc]) => {
    if (!svc?.status) return false;
    if (IGNORABLE_STATUSES.includes(svc.status)) return false;
    if (OPTIONAL_SERVICES.includes(key) && svc.status === "not_configured") return false;
    return true;
  });

  if (scorable.length === 0) return { score: 0, grade: "red" };

  let score = 100;

  for (const [key, svc] of scorable) {
    const status = svc.status;
    if (status === "failed") {
      if (key === "authentication") score = Math.min(score, 40);
      else if (key === "firestore") score = Math.min(score, 60);
      else if (key === "storage") score = Math.min(score, 70);
      else if (OPTIONAL_SERVICES.includes(key)) score = Math.min(score, 90);
      else score = Math.min(score, 50);
    }
    if (status === "permission_denied" && key === "firestore") score = Math.min(score, 60);
    if (status === "read_only") score = Math.min(score, 85);
  }

  const coreTested = CORE_SERVICES.filter((k) => services[k] && !IGNORABLE_STATUSES.includes(services[k]?.status));
  const coreOk = coreTested.every((k) =>
    ["success", "permission_denied", "read_only", "not_signed_in"].includes(services[k]?.status)
  );
  if (coreOk && coreTested.length === CORE_SERVICES.length && score < 100) {
    const optionalFailed = OPTIONAL_SERVICES.some((k) => services[k]?.status === "failed");
    if (!optionalFailed) score = Math.max(score, 95);
  }

  if (scorable.every(([, svc]) => svc.status === "success" || svc.status === "not_configured")) {
    score = 100;
  }

  const grade = score >= 95 ? "green" : score >= 70 ? "yellow" : "red";
  return { score, grade };
}

function deriveOverallStatus(healthScore) {
  if (healthScore <= 0) return "failed";
  if (healthScore < 70) return "failed";
  if (healthScore < 95) return "warning";
  return "online";
}

function buildDiagnostics(conn, configIssues, engineDiagnostics, warnings) {
  const diagnostics = [...engineDiagnostics];

  (configIssues || []).forEach((issue) => {
    diagnostics.push({
      category: "configuration",
      severity: issue.severity,
      message: issue.message,
      field: issue.field,
    });
  });

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    diagnostics.push({ category: "network", severity: "error", message: "Browser reports offline" });
  }

  if (conn?.apiKey && conn.apiKey !== SECRET_MASK && conn.apiKey.length < 20) {
    warnings.push("API key appears unusually short");
    diagnostics.push({ category: "security", severity: "warning", message: "API key format suspicious" });
  }

  return diagnostics;
}

export async function verifyStudioConnection(connectionOrId, options = {}) {
  const started = performance.now();
  const conn = await resolveConnectionRuntimeConfig(connectionOrId);

  const warnings = [];
  const diagnostics = [];

  if (!conn) {
    return {
      result: buildVerifyResult({
        conn: null, checks: [{ service: "configuration", status: "failed", message: "Connection not found" }],
        services: {}, diagnostics, warnings, started, options, healthScore: 0, grade: "red",
      }),
      status: "failed",
    };
  }

  const configValidation = validateConfiguration(conn);
  const configIssues = configValidation.issues;
  if (!configValidation.allRequiredOk) {
    const checks = configIssues.map((i) => ({
      service: "configuration", status: "failed", message: i.message, latencyMs: 0,
    }));
    const diag = buildDiagnostics(conn, configIssues, diagnostics, warnings);
    const { score, grade } = calculateHealthScore(
      { configuration: makeServiceResult("failed", configIssues[0]?.message) },
      diag
    );
    return {
      result: buildVerifyResult({
        conn, checks,
        services: { configuration: makeServiceResult("failed", configIssues[0]?.message) },
        diagnostics: diag, warnings, started, options, healthScore: score, grade,
      }),
      status: "failed",
    };
  }

  if (!hasLiveCredentials(conn)) {
    warnings.push("API key not available for live SDK checks — configuration validated only.");
    const services = {
      configuration: makeServiceResult("success", "Configuration validated (secrets masked)"),
      authentication: makeServiceResult("not_configured", "Not Configured"),
      firestore: makeServiceResult("not_configured", "Not Configured"),
      storage: makeServiceResult("not_configured", "Not Configured"),
      functions: makeServiceResult("not_configured", "Not Configured"),
      hosting: makeServiceResult("not_configured", "Hosting not configured."),
    };
    const checks = Object.entries(services).map(([service, r]) => ({ service, ...r }));
    const diag = buildDiagnostics(conn, configIssues, diagnostics, warnings);
    const { score, grade } = calculateHealthScore(services, diag);
    return {
      result: buildVerifyResult({
        conn, checks, services, diagnostics: diag, warnings, started, options,
        healthScore: score, grade, permissions: null, collections: null, indexes: null, security: [],
      }),
      status: deriveOverallStatus(score),
    };
  }

  const initT0 = performance.now();

  try {
    const runtime = await resolveConnectionTestRuntime(conn.id, { suite: "health_check" });
    const initLatencyMs = Math.round(performance.now() - initT0);

    const engine = await runFullEngine(runtime, conn, initLatencyMs);
    const diag = buildDiagnostics(conn, configIssues, diagnostics, warnings);

    engine.permissions && Object.values(engine.permissions).forEach((p) => {
      if (p.status === "read_only") {
        warnings.push("Firestore: Read-only access — write/delete not permitted");
      }
    });

    const { score, grade } = calculateHealthScore(engine.services, diag);
    const result = buildVerifyResult({
      conn,
      checks: engine.checks,
      services: engine.services,
      diagnostics: diag,
      warnings,
      started,
      options,
      healthScore: score,
      grade,
      appName: runtime.app?.name,
      runtimeSnapshot: runtime.snapshot,
      authSnapshot: runtime.authSnapshot,
      executionMode: runtime.executionMode,
      permissions: engine.permissions,
      collections: engine.collections,
      indexes: engine.indexes,
      security: engine.security,
      performance: { ...engine.performance, total: Math.round(performance.now() - started) },
      configFields: engine.configFields,
    });

    const status = deriveOverallStatus(score);

    if (conn.id) {
      saveConnectionVerificationHistory({
        connectionId: conn.id,
        projectId: conn.firebaseProjectId,
        status,
        healthScore: score,
        services: engine.services,
        latency: result.latency,
        verifiedAt: result.checkedAt,
        verifiedBy: options.verifiedBy || "",
        browser: result.advanced?.browser,
        os: result.advanced?.os,
        version: FIREBASE_SDK_VERSION,
        report: result,
      }).catch(() => {});
    }

    return { result, status };
  } catch (e) {
    const diag = buildDiagnostics(conn, configIssues, [
      { category: "sdk", severity: "error", message: e?.message || "Verification engine failed" },
    ], warnings);

    if (String(e?.message || "").includes("duplicate")) {
      diag.push({ category: "security", severity: "warning", message: "Duplicate Firebase app name detected" });
    }

    return {
      result: buildVerifyResult({
        conn,
        checks: [{ service: "configuration", status: "failed", message: e?.message, latencyMs: 0 }],
        services: {},
        diagnostics: diag,
        warnings,
        started,
        options,
        healthScore: 0,
        grade: "red",
      }),
      status: "failed",
    };
  }
}

function buildVerifyResult({
  conn,
  checks,
  services,
  diagnostics,
  warnings,
  started,
  options,
  healthScore,
  grade,
  appName,
  runtimeSnapshot,
  authSnapshot,
  executionMode,
  permissions,
  collections,
  indexes,
  security,
  performance: perf,
  configFields,
}) {
  const responseTimeMs = Math.round(performance.now() - started);
  const checkedAt = nowIso();

  const advanced = {
    sdkVersion: FIREBASE_SDK_VERSION,
    projectId: conn?.firebaseProjectId || "",
    region: conn?.region || "(default)",
    database: "(default)",
    storageBucket: conn?.storageBucket || "—",
    appName: appName || runtimeSnapshot?.runtimeAppName || "",
    runtimeSnapshot: runtimeSnapshot || null,
    authSnapshot: authSnapshot || null,
    executionMode: executionMode || null,
    verificationTimeMs: responseTimeMs,
    verifiedAt: checkedAt,
    verifiedBy: options.verifiedBy || "—",
    browser: detectBrowser(),
    os: detectOs(),
    configFields: configFields || {},
  };

  const latency = {
    init: perf?.init ?? 0,
    authentication: perf?.auth ?? services?.authentication?.latencyMs ?? 0,
    firestore: perf?.firestore ?? services?.firestore?.latencyMs ?? 0,
    storage: perf?.storage ?? services?.storage?.latencyMs ?? 0,
    functions: perf?.functions ?? services?.functions?.latencyMs ?? 0,
    hosting: perf?.hosting ?? services?.hosting?.latencyMs ?? 0,
    total: perf?.total ?? responseTimeMs,
  };

  const result = {
    overall: deriveOverallStatus(healthScore),
    healthScore,
    healthGrade: grade,
    responseTimeMs,
    checkedAt,
    checks,
    services,
    diagnostics,
    warnings,
    latency,
    advanced,
    permissions: permissions || null,
    collections: collections || null,
    indexes: indexes || null,
    security: security || [],
    performance: perf || latency,
    authLatencyMs: latency.authentication,
    firestoreLatencyMs: latency.firestore,
    storageLatencyMs: latency.storage,
    engine: "firebase-sdk-health-suite",
  };

  result.raw = JSON.parse(JSON.stringify(result));
  return result;
}
